import { useLocation, Link } from "@tanstack/react-router";
import { useTheme } from "./ThemeProvider";
import { ChevronRight, Home } from "lucide-react";

const routeLabels: Record<string, string> = {
  "/": "Home",
  "/brand": "Brand & Logos",
  "/colors": "Color System",
  "/typography": "Typography",
  "/components": "Components",
  "/grid": "Grid System",
  "/responsive": "Responsive System",
  "/navigation": "Interface",
  "/dashboard": "Metrics",
  "/motion": "Motion & Fluency",
  "/gamification": "Gamification",
  "/tokens": "Design Tokens",
};

export function Breadcrumbs() {
  const location = useLocation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const pathSegments = location.pathname.split("/").filter(Boolean);

  // Build breadcrumb trail
  const breadcrumbs = [
    { label: "Home", path: "/" }
  ];

  let currentPath = "";
  pathSegments.forEach((segment) => {
    currentPath += `/${segment}`;
    const label = routeLabels[currentPath] ?? segment;
    breadcrumbs.push({ label, path: currentPath });
  });

  // Don't show breadcrumbs on home page
  if (location.pathname === "/") return null;

  // Mobile: Show only Home > ... > Current if more than 3 items
  const isMobile = breadcrumbs.length > 3;
  const mobileBreadcrumbs = isMobile
    ? [breadcrumbs[0]!, { label: "...", path: "#" }, breadcrumbs[breadcrumbs.length - 1]!]
    : breadcrumbs;

  return (
    <div className="mb-6 flex items-center gap-2 text-sm overflow-x-auto scrollbar-hide pb-2">
      {/* Desktop: Full breadcrumbs */}
      <div className="hidden md:flex items-center gap-2">
        {breadcrumbs.map((crumb, idx) => (
          <div key={crumb.path} className="flex items-center gap-2">
            {idx > 0 && (
              <ChevronRight size={14} className={isDark ? "text-muted-foreground" : "text-muted-foreground"} />
            )}
            {idx === breadcrumbs.length - 1 ? (
              <span className={isDark ? "text-white" : "text-muted-foreground"}>
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className={`flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                  isDark
                    ? "text-muted-foreground hover:text-primary"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {idx === 0 && <Home size={14} />}
                {crumb.label}
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Mobile: Truncated breadcrumbs */}
      <div className="flex md:hidden items-center gap-2">
        {mobileBreadcrumbs.map((crumb, idx) => {
          if (!crumb) return null;
          return (
          <div key={crumb.path + idx} className="flex items-center gap-2">
            {idx > 0 && (
              <ChevronRight size={14} className={isDark ? "text-muted-foreground" : "text-muted-foreground"} />
            )}
            {idx === mobileBreadcrumbs.length - 1 ? (
              <span className={`${isDark ? "text-white" : "text-muted-foreground"} whitespace-nowrap`}>
                {crumb.label}
              </span>
            ) : crumb.label === "..." ? (
              <span className={`${isDark ? "text-muted-foreground" : "text-muted-foreground"} whitespace-nowrap`}>
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className={`flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                  isDark
                    ? "text-muted-foreground hover:text-primary"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {idx === 0 && <Home size={14} />}
                {crumb.label}
              </Link>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
